import React, { Component, ErrorInfo, ReactNode } from "react";
import { isFunction, isNil, isRegExp, isString } from "lodash";
import NextErrorComponent from "next/error";

import { SnackbarContext } from "@components/SnackbarProvider";

interface ErrorMessageAction {
  title: string;
  handler: () => void;
}

interface ErrorMessage {
  title: string;
  description: string;
  handled?: boolean;
  action?: ErrorMessageAction;
}
type ErrorMessageFunction<P> = (
  error: Error,
  props?: P
) => ErrorMessage | undefined;
type SupportedErrorSelectors = string | RegExp | ((error: Error) => boolean);
type SupportedErrorMessages<P> = ErrorMessage | ErrorMessageFunction<P>;
type ErrorMapping<P> =
  | ErrorMessageFunction<P>
  | {
      selector: SupportedErrorSelectors;
      message: SupportedErrorMessages<P>;
    };

type ErrorSource = "FROM_EVENT" | "FROM_BOUNDARY";
interface State {
  hasError: boolean;
  errorMessage?: ErrorMessage;
  source?: ErrorSource;
  originalError?: Error;
}

const ERROR_EVENT = "error";
const UNHANDLED_REJECTION_EVENT = "unhandledrejection";

const withErrorBoundary = <P,>(
  WrappedComponent: React.ComponentType<P>,
  errorMappings: Array<ErrorMapping<P>> = []
): React.ComponentType<P> => {
  class ComponentWithErrorBoundary extends Component<P, State> {
    static contextType = SnackbarContext;
    public state: State = {
      hasError: false,
    };

    private getMessage = (
      error: Error,
      message: SupportedErrorMessages<P>,
      defaultHandled = true
    ): ErrorMessage => {
      const defaultErrorMessage: ErrorMessage = {
        handled: defaultHandled,
        title: "",
        description: "",
      };
      const errorMessage = isFunction(message)
        ? message(error, this.props) || defaultErrorMessage
        : message;
      if (isNil(errorMessage.handled)) {
        errorMessage.handled = defaultHandled;
      }
      return errorMessage;
    };

    private getErrorMessageFromError = (error: Error): ErrorMessage => {
      for (const errorMapping of errorMappings) {
        if (isFunction(errorMapping)) {
          const errorMessageFromFunction = this.getMessage(
            error,
            errorMapping,
            false
          );
          if (errorMessageFromFunction.handled) {
            return errorMessageFromFunction;
          }
          continue;
        }

        const { selector, message } = errorMapping;

        if (
          isString(selector) &&
          (error.name.includes(selector) || error.message.includes(selector))
        ) {
          return this.getMessage(error, message);
        }
        if (isRegExp(selector) && selector.test(error.message)) {
          return this.getMessage(error, message);
        }
        if (isFunction(selector) && selector(error)) {
          return this.getMessage(error, message);
        }
      }

      return {
        title: "Error",
        description: error.message || (error as unknown as string),
        handled: false,
      };
    };

    private getErrorFromEvent = (ev: PromiseRejectionEvent | ErrorEvent) => {
      if (ev.type === UNHANDLED_REJECTION_EVENT) {
        const error = (ev as PromiseRejectionEvent).reason;
        return error?.response?.data?.error || error;
      }

      const error = (ev as ErrorEvent).error;
      return error?.response?.data?.error || error;
    };

    private handleDocumentError = (ev: PromiseRejectionEvent | ErrorEvent) => {
      const originalError = this.getErrorFromEvent(ev);
      const errorMessage = this.getErrorMessageFromError(originalError);

      if (errorMessage.handled) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }

      this.setState({
        hasError: true,
        errorMessage,
        source: "FROM_EVENT",
        originalError,
      });

      const { showWarning } = this.context;
      showWarning(
        errorMessage.title,
        errorMessage.description,
        errorMessage?.action?.title,
        errorMessage?.action?.handler
      );
    };

    public componentDidMount() {
      window.addEventListener(ERROR_EVENT, this.handleDocumentError);
      window.addEventListener(
        UNHANDLED_REJECTION_EVENT,
        this.handleDocumentError
      );
    }

    public componentWillUnmount() {
      window.removeEventListener(ERROR_EVENT, this.handleDocumentError);
      window.removeEventListener(
        UNHANDLED_REJECTION_EVENT,
        this.handleDocumentError
      );
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
      console.error("Uncaught error:", error, errorInfo);
      this.setState({
        hasError: true,
        errorMessage: this.getErrorMessageFromError(error),
        source: "FROM_BOUNDARY",
        originalError: error,
      });
    }

    public render(): ReactNode {
      const props = this.props;
      const { hasError, errorMessage, source, originalError } = this.state;

      if (hasError && source === "FROM_BOUNDARY") {
        if (errorMessage.handled) {
          return (
            <NextErrorComponent
              statusCode={500}
              title={errorMessage.description}
            />
          );
        }

        throw originalError;
      }

      return <WrappedComponent {...(props as P)} />;
    }
  }
  return ComponentWithErrorBoundary;
};

export default withErrorBoundary;
